import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";

const TYPES = [
  { code: "RETAIL", label: "Retail" },
  { code: "RESELLER", label: "Reseller" },
  { code: "WHOLESALE", label: "Grosir" },
  { code: "OTHER", label: "Lainnya" },
];
export const typeLabel = (t) => (TYPES.find((x) => x.code === t) || {}).label || t;

const EMPTY = {
  customer_name: "", customer_type: "RETAIL", pic_name: "", phone: "", whatsapp: "", email: "",
  billing_address: "", billing_city: "", shipping_address: "", shipping_city: "", note: "",
};

export default function MasterKonsumen() {
  const { user } = useAuth();
  const canView = user && (user.role === "OWNER" || user.role === "ADMIN_INVENTORY");
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null); // null = list view; object = create/edit form
  const [editId, setEditId] = useState(null);
  const [sameAddr, setSameAddr] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/customers").then(({ data }) => setList(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY }); setEditId(null); setSameAddr(false); setError(""); };
  const openEdit = (c) => {
    setForm({
      customer_name: c.customer_name || "", customer_type: c.customer_type || "RETAIL",
      pic_name: c.pic_name || "", phone: c.phone || "", whatsapp: c.whatsapp || "", email: c.email || "",
      billing_address: c.billing_address || "", billing_city: c.billing_city || "",
      shipping_address: c.shipping_address || "", shipping_city: c.shipping_city || "", note: c.note || "",
    });
    setEditId(c.id); setSameAddr(false); setError("");
  };
  const cancel = () => { setForm(null); setEditId(null); setError(""); };

  const save = async () => {
    setError("");
    const payload = { ...form };
    if (sameAddr) { payload.shipping_address = form.billing_address; payload.shipping_city = form.billing_city; }
    if (!payload.email) delete payload.email;
    try {
      if (editId) await api.patch(`/customers/${editId}`, payload);
      else await api.post("/customers", payload);
      cancel(); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleStatus = async (c) => {
    setError("");
    const next = c.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    if (!window.confirm(`${next === "INACTIVE" ? "Nonaktifkan" : "Aktifkan"} konsumen ${c.customer_code} — ${c.customer_name}?`)) return;
    try { await api.patch(`/customers/${c.id}`, { status: next }); load(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (user && !canView) {
    return <div><AppTopBar active="customers" /><main className="page"><p className="hint">Akses ditolak.</p></main></div>;
  }

  const filtered = list.filter((c) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [c.customer_code, c.customer_name, c.pic_name, c.phone, c.whatsapp, c.billing_city, c.shipping_city]
      .some((v) => (v || "").toLowerCase().includes(s));
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <AppTopBar active="customers" />
      <main className="page" style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div className="page-head">
          <h1 className="page-title">Master Konsumen</h1>
          {!form && <button className="btn-primary" onClick={openNew} data-testid="cust-new-button">Tambah Konsumen</button>}
        </div>
        {error && <div className="alert-error" data-testid="cust-error">{error}</div>}

        {form ? (
          <section className="panel" data-testid="cust-form-panel">
            <h2 className="panel-title">{editId ? "Edit Konsumen" : "Konsumen Baru"}</h2>

            <h3 className="field-label" style={{ marginTop: 4 }}>Identitas</h3>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label className="field-label">Nama *</label>
                <input className="field-input" value={form.customer_name} onChange={set("customer_name")} data-testid="cust-customer_name-input" />
              </div>
              <div style={{ width: 180 }}>
                <label className="field-label">Tipe</label>
                <select className="field-input" value={form.customer_type} onChange={set("customer_type")} data-testid="cust-type-select">
                  {TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label className="field-label">PIC (opsional)</label>
                <input className="field-input" value={form.pic_name} onChange={set("pic_name")} data-testid="cust-pic_name-input" />
              </div>
            </div>

            <h3 className="field-label" style={{ marginTop: 12 }}>Kontak</h3>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label className="field-label">Phone</label>
                <input className="field-input" placeholder="08xxxx" value={form.phone} onChange={set("phone")} data-testid="cust-phone-input" />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label className="field-label">WhatsApp</label>
                <input className="field-input" placeholder="08xxxx" value={form.whatsapp} onChange={set("whatsapp")} data-testid="cust-whatsapp-input" />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label className="field-label">Email</label>
                <input className="field-input" type="email" placeholder="nama@perusahaan.com" value={form.email} onChange={set("email")} data-testid="cust-email-input" />
              </div>
            </div>

            <h3 className="field-label" style={{ marginTop: 12 }}>Alamat Penagihan</h3>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <div style={{ flex: 2, minWidth: 220 }}>
                <label className="field-label">Alamat</label>
                <input className="field-input" value={form.billing_address} onChange={set("billing_address")} data-testid="cust-billing_address-input" />
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <label className="field-label">Kota</label>
                <input className="field-input" value={form.billing_city} onChange={set("billing_city")} data-testid="cust-billing_city-input" />
              </div>
            </div>

            <h3 className="field-label" style={{ marginTop: 12 }}>Alamat Pengiriman
              <label style={{ marginLeft: 12, fontWeight: 400 }}>
                <input type="checkbox" checked={sameAddr} onChange={(e) => setSameAddr(e.target.checked)} data-testid="cust-sameaddr-checkbox" /> sama dengan alamat penagihan
              </label>
            </h3>
            {!sameAddr && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <div style={{ flex: 2, minWidth: 220 }}>
                  <label className="field-label">Alamat</label>
                  <input className="field-input" value={form.shipping_address} onChange={set("shipping_address")} data-testid="cust-shipping_address-input" />
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <label className="field-label">Kota</label>
                  <input className="field-input" value={form.shipping_city} onChange={set("shipping_city")} data-testid="cust-shipping_city-input" />
                </div>
              </div>
            )}

            <h3 className="field-label" style={{ marginTop: 12 }}>Catatan</h3>
            <input className="field-input" value={form.note} onChange={set("note")} data-testid="cust-note-input" />

            <div className="form-actions" style={{ marginTop: 14 }}>
              <button className="btn-primary" onClick={save} data-testid="cust-save-button">{editId ? "Simpan Perubahan" : "Simpan Konsumen"}</button>
              <button className="btn-ghost" onClick={cancel} data-testid="cust-cancel-button">Batal</button>
            </div>
          </section>
        ) : (
          <section className="panel" data-testid="cust-list-panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>
              <h2 className="panel-title" style={{ margin: 0 }}>Daftar Konsumen ({filtered.length}{q ? `/${list.length}` : ""})</h2>
              <input className="field-input" style={{ maxWidth: 280 }} placeholder="Cari kode / nama / kota…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cust-search-input" />
            </div>
            <div className="table-scroll">
              <table className="data-table" data-testid="cust-list-table">
                <thead><tr><th>Kode</th><th>Nama</th><th>Tipe</th><th>PIC</th><th>WA / Phone</th><th>Kota</th><th>Status</th><th>Aksi</th></tr></thead>
                <tbody>
                  {filtered.length === 0 && <tr><td colSpan={8} className="empty-cell">Belum ada konsumen.</td></tr>}
                  {filtered.map((c) => (
                    <tr key={c.id} data-testid={`cust-row-${c.customer_code}`}>
                      <td className="mono">{c.customer_code}</td>
                      <td>{c.customer_name}</td>
                      <td>{typeLabel(c.customer_type)}</td>
                      <td>{c.pic_name || "-"}</td>
                      <td>{c.whatsapp || c.phone || "-"}</td>
                      <td>{c.shipping_city || c.billing_city || "-"}</td>
                      <td><span className={`status-pill ${c.status === "ACTIVE" ? "on" : "off"}`} data-testid={`cust-status-${c.customer_code}`}>{c.status}</span></td>
                      <td className="actions-cell">
                        <button className="btn-mini" onClick={() => openEdit(c)} data-testid={`cust-edit-${c.customer_code}`}>Detail/Edit</button>
                        <button className="btn-mini" onClick={() => toggleStatus(c)} data-testid={`cust-toggle-${c.customer_code}`}>{c.status === "ACTIVE" ? "Nonaktifkan" : "Aktifkan"}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
